import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import './sideMenu.css';
import { ROLE, USER_TYPE, UPLOAD_TYPE, CONFIG } from '../../../shared/constants/common';
import { ROUTES } from '../../../shared/constants/routes';
import useNavigation from '../../../shared/utils/useNavigation';
import { useTheme } from '../../../features/contexts/ThemeContext';
// import { clearAllCookies } from '../../../shared/utils/cookies';
import {
  isUserLoggedIn,
  getUserRole,
  getUserStatus,
  getUserProfilePicture,
  isUserAdmin
} from '../../../shared/utils/function';
import PhotoUploader from '../../PhotoUploader';
import { markSlideMenuOpen } from '../../../shared/utils/navigationState';
import { clearAllUserState } from '../../../shared/utils/authUtils';
import SocialInfo from '../../../views/component/SocialInfo';
import LazyImage from '../../../components/LazyImage';
import { getPendingCollectTips } from '../../../shared/actions/tipAction';
import { getAudienceThreshold } from '../../../shared/actions/creatorActions';

const SideMenu = ({
  isOpen,
  onClose,
  user,
  creator,
  dispatch
}) => {
  const navigate = useNavigation();
  const isLoggedIn = isUserLoggedIn();
  const role = getUserRole();
  const userStatus = getUserStatus();
  const isAdmin = isUserAdmin();
  const { toggleDarkMode, isDarkMode } = useTheme();

  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [pendingCollectData, setPendingCollectData] = useState(null);
  const [audienceThresholdData, setAudienceThresholdData] = useState(null);
  const [isMassMessagingDismissed, setIsMassMessagingDismissed] = useState(false);

  const isCreatorRole = role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT;
  const hasPendingCollect = Number(pendingCollectData?.pendingAmount) > 0;
  const showMassMessagingBanner =
    audienceThresholdData?.meetsThreshold === true && !isMassMessagingDismissed;

  useEffect(() => {
    if (!isOpen || !isLoggedIn || !isCreatorRole) {
      return;
    }

    let isCancelled = false;

    const fetchCreatorMenuData = async () => {
      const [pendingResult, audienceResult] = await Promise.all([
        getPendingCollectTips(),
        getAudienceThreshold(),
      ]);

      if (isCancelled) {
        return;
      }

      if (pendingResult.success) {
        setPendingCollectData(pendingResult.data);
      }

      if (audienceResult.success) {
        setAudienceThresholdData(audienceResult.data);
      }
    };

    fetchCreatorMenuData();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, isLoggedIn, isCreatorRole]);

  // Set active menu item based on current URL
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === ROUTES.CREATOR_NOTIFICATIONS && (role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT)) {
      setActiveMenuItem('my-notifications');
    } else if (currentPath === ROUTES.NOTIFICATIONS && role === ROLE.USER) {
      setActiveMenuItem('notifications');
    }
  }, [role]);

  // Get username (truncate to 9 chars + "…" so "Refer & Earn" fits)
  const getUsername = () => {
    let name = '@guest';
    if (userStatus === USER_TYPE.CREATOR && creator && creator.user_data && creator.user_data.username) {
      name = `@${creator.user_data.username}`;
    } else if (isLoggedIn && user && user.username) {
      name = `@${user.username}`;
    }
    return name.length > 9 ? `${name.slice(0, 9)}…` : name;
  };

  // Handle menu item click
  const handleLogin = () => {
    markSlideMenuOpen(); // Track that menu was open before navigation
    onClose(); // Close menu before navigating
    navigate.toLogin();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSignup = () => {
    markSlideMenuOpen(); // Track that menu was open before navigation
    onClose(); // Close menu before navigating
    navigate.toSignup();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMenuItemClick = (item, url = null) => {
    setActiveMenuItem(item);
    if (url) {
      markSlideMenuOpen(); // Track that menu was open before navigation
      onClose(); // Close menu before navigating
      navigate.to(url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoToMessageList = () => {
    markSlideMenuOpen();
    onClose();
    navigate.toMessages('unread-priority');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMessageListArrowKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleGoToMessageList();
    }
  };

  // Handle logout — hard redirect avoids React unmount errors on protected routes
  const handleLogout = () => {
    clearAllUserState(dispatch, { redirectToLogin: true });
  };

  if (!isOpen) return null;

  return (
    <>


      {/* Side Menu */}
      <div className={`modern-side-menu-container`}>
        {/* Header */}
        <header className="modern-side-menu-header">
          <div className="modern-side-menu-user-info">
            <div className="modern-side-menu-avatar-container">
              <div className="modern-side-menu-avatar-wrapper">
                <div className="modern-side-menu-avatar-inner">
                  <LazyImage
                    src={getUserProfilePicture(user, creator)}
                    alt="User avatar"
                    className="modern-side-menu-avatar-img"
                  />
                </div>
              </div>

              {/* TODO: Uncomment this when we have a way to upload profile picture */}
              {isLoggedIn && role === ROLE.USER && (
                <PhotoUploader
                  uploadType={UPLOAD_TYPE.PICTURE}
                  menuPosition="bottom-left"
                  showButton={false}
                  className="modern-side-menu-edit-badge"
                  uploadingText="Uploading Profile Picture..."
                >

                  <i className="fa-solid fa-pen-to-square"></i>
                </PhotoUploader>
              )}

            </div>

            <h1 className="modern-side-menu-username">
              {getUsername()}
              {userStatus === USER_TYPE.CREATOR && (
                <button
                  className='btn btn_primary_outline_pink'
                  onClick={() => handleMenuItemClick('referrals', ROUTES.REFERRALS)}
                >
                  <i className='fa-solid fa-dollar-sign'></i>
                  Refer & Earn
                </button>
              )}
            </h1>
          </div>
          <button className="modern-side-menu-close-btn" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </header>

        {/* Main Menu */}
        <main className="modern-side-menu-main">

          {userStatus === USER_TYPE.CREATOR && (hasPendingCollect || showMassMessagingBanner) && (
            <div className='border_bottom_gray pb-4'>
              {hasPendingCollect && (
                <div className='cm_green_card mb-3'>
                  <div>
                    <span className='cm_green_card_icon'><i className='fa-solid fa-dollar-sign'></i></span>
                  </div>

                  <div className='w-full'>
                    <h4>
                      ${pendingCollectData.pendingAmountFormatted} pending to collect
                    </h4>
                    <p>
                      Reply to first-time priority messages to claim your tip. After that, all future tips and
                      purchases are collected automatically.
                    </p>
                  </div>

                  <div
                    role='button'
                    tabIndex={0}
                    aria-label='Go to message list'
                    onClick={handleGoToMessageList}
                    onKeyDown={handleMessageListArrowKeyDown}
                  >
                    <span>
                      <i className='fa-solid fa-chevron-right'></i>
                    </span>
                  </div>
                </div>
              )}

              {showMassMessagingBanner && (
                <div className='cm_pink_card cm_pink_card_with_action mb-3'>
                  <span
                    className='cm_pink_card_cross'
                    role='button'
                    tabIndex={0}
                    aria-label='Dismiss mass messaging notification'
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsMassMessagingDismissed(true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsMassMessagingDismissed(true);
                      }
                    }}
                  >
                    <i className='fa-solid fa-xmark'></i>
                  </span>
                  <div className='w-full'>
                    <h4>Mass messaging unlocked 🎉</h4>
                    <p>
                      You reached {audienceThresholdData?.threshold ?? 100} subscribers! You can now mass message
                      users in your unread priority inbox to collect your tips.
                    </p>
                  </div>
                  <div
                    role='button'
                    tabIndex={0}
                    aria-label='Go to message list'
                    onClick={handleGoToMessageList}
                    onKeyDown={handleMessageListArrowKeyDown}
                  >
                    <span>
                      <i className='fa-solid fa-chevron-right'></i>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings Section */}
          <section className="modern-side-menu-section">
            <div className="modern-side-menu-section-header">
              <div className="modern-side-menu-section-title-wrapper">
                <div className="modern-side-menu-section-icon">
                  <i className="fa-solid fa-cog"></i>
                </div>
                <h2 className="modern-side-menu-section-title">Settings</h2>
              </div>
              {/* <button 
                className="modern-side-menu-theme-toggle"
                onClick={toggleDarkMode}
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                <i className="fa-solid fa-cog"></i>
              </button> */}
            </div>
            <div className="modern-side-menu-items">
              {!isLoggedIn ? (
                // Guest user - show only Login and Signup
                <>
                  <div
                    className={`modern-side-menu-item ${activeMenuItem === 'login' ? 'active' : ''}`}
                    onClick={() => handleLogin()}
                  >
                    <div className="modern-side-menu-item-content">
                      <div className="modern-side-menu-item-icon">
                        <i className="fa-solid fa-sign-in-alt"></i>
                      </div>
                      <span className="modern-side-menu-item-text">Login</span>
                    </div>
                    <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                  </div>
                  <div
                    className={`modern-side-menu-item ${activeMenuItem === 'signup' ? 'active' : ''}`}
                    onClick={() => handleSignup()}
                  >
                    <div className="modern-side-menu-item-content">
                      <div className="modern-side-menu-item-icon">
                        <i className="fa-solid fa-user-plus"></i>
                      </div>
                      <span className="modern-side-menu-item-text">Signup</span>
                    </div>
                    <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                  </div>
                </>
              ) : (
                // Logged in user - show main navigation options
                <>
                  {userStatus === USER_TYPE.CREATOR && (
                    <>
                      <div
                        className={`modern-side-menu-item ${activeMenuItem === 'earnings-payout' ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick('earnings-payout', ROUTES.EARNINGS_PAYOUT)}
                      >
                        <div className="modern-side-menu-item-content">
                          <div className="modern-side-menu-item-icon">
                            <i className="fa-solid fa-chart-line"></i>
                          </div>
                          <span className="modern-side-menu-item-text">
                            Earnings & Payout
                          </span>
                        </div>
                        <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                      </div>

                      <div
                        className={`modern-side-menu-item ${activeMenuItem === 'referrals' ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick('referrals', ROUTES.REFERRALS)}
                      >
                        <div className="modern-side-menu-item-content">
                          <div className="modern-side-menu-item-icon">
                            <i className="fa-solid fa-users"></i>
                          </div>
                          <span className="modern-side-menu-item-text">Referrals</span>
                        </div>
                        <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                      </div>

                      <div
                        className={`modern-side-menu-item ${activeMenuItem === 'tags' ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick('tags', ROUTES.PROFILE_TAGS)}
                      >
                        <div className="modern-side-menu-item-content">
                          <div className="modern-side-menu-item-icon">
                            <i className="fa-solid fa-tags"></i>
                          </div>
                          <span className="modern-side-menu-item-text">Tags</span>
                        </div>
                        <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                      </div>
                    </>
                  )}

                  {!isAdmin && (
                    <div
                      className={`modern-side-menu-item ${activeMenuItem === 'edit-username' ? 'active' : ''}`}
                      onClick={() => handleMenuItemClick('edit-username', ROUTES.EDIT_USERNAME)}
                    >
                      <div className="modern-side-menu-item-content">
                        <div className="modern-side-menu-item-icon">
                          <i className="fa-solid fa-user"></i>
                        </div>
                        <span className="modern-side-menu-item-text">Account</span>
                      </div>
                      <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                    </div>
                  )}

                  <div
                    className={`modern-side-menu-item ${activeMenuItem === 'change-password' ? 'active' : ''}`}
                    onClick={() => handleMenuItemClick('change-password', ROUTES.CHANGE_PASSWORD)}
                  >
                    <div className="modern-side-menu-item-content">
                      <div className="modern-side-menu-item-icon">
                        <i className="fa-solid fa-lock"></i>
                      </div>
                      <span className="modern-side-menu-item-text">Change Password</span>
                    </div>
                    <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                  </div>

                  {(role == ROLE.CREATOR_SUB || role == ROLE.CREATOR_CONTENT) && (
                    <div
                      className={`modern-side-menu-item ${activeMenuItem === 'my-notifications' ? 'active' : ''}`}
                      onClick={() => handleMenuItemClick('my-notifications', ROUTES.CREATOR_NOTIFICATIONS)}
                    >
                      <div className="modern-side-menu-item-content">
                        <div className="modern-side-menu-item-icon">
                          <i className="fa-solid fa-bell"></i>
                        </div>
                        <span className="modern-side-menu-item-text">Notifications</span>
                      </div>
                      <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                    </div>
                  )}

                  {/* User Notifications - For role 2 (normal users) */}
                  {role == ROLE.USER && (
                    <div
                      className={`modern-side-menu-item ${activeMenuItem === 'notifications' ? 'active' : ''}`}
                      onClick={() => handleMenuItemClick('notifications', ROUTES.NOTIFICATIONS)}
                    >
                      <div className="modern-side-menu-item-content">
                        <div className="modern-side-menu-item-icon">
                          <i className="fa-solid fa-bell"></i>
                        </div>
                        <span className="modern-side-menu-item-text">Notifications</span>
                      </div>
                      <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                    </div>
                  )}

                  {userStatus === USER_TYPE.USER && (
                    <>
                      <div
                        className={`modern-side-menu-item ${activeMenuItem === 'wallet' ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick('wallet', ROUTES.WALLET)}
                      >
                        <div className="modern-side-menu-item-content">
                          <div className="modern-side-menu-item-icon">
                            <i className="fa-solid fa-wallet"></i>
                          </div>
                          <span className="modern-side-menu-item-text">Wallet</span>
                        </div>
                        <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                      </div>

                      <div
                        className={`modern-side-menu-item ${activeMenuItem === 'transactions' ? 'active' : ''}`}
                        onClick={() => handleMenuItemClick('transactions', ROUTES.TRANSACTIONS)}
                      >
                        <div className="modern-side-menu-item-content">
                          <div className="modern-side-menu-item-icon">
                            <i className="fa-solid fa-credit-card"></i>
                          </div>
                          <span className="modern-side-menu-item-text">Transactions</span>
                        </div>
                        <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Legal Section */}
          <section className="modern-side-menu-section">
            <div className="modern-side-menu-section-header">
              <div className="modern-side-menu-section-title-wrapper">
                <div className="modern-side-menu-section-icon">
                  <i className="fa-solid fa-gavel"></i>
                </div>
                <h2 className="modern-side-menu-section-title">Legal</h2>
              </div>
            </div>
            <div className="modern-side-menu-items">
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'terms' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('terms', ROUTES.LEGAL_TERMS)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-file-contract"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Terms of Service</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'privacy' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('privacy', ROUTES.LEGAL_PRIVACY)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-shield-alt"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Privacy Policy</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'ccpa' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('ccpa', ROUTES.LEGAL_CCPA)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-map-marked-alt"></i>
                  </div>
                  <span className="modern-side-menu-item-text">California Privacy Policy</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'creator-agreement' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('creator-agreement', ROUTES.LEGAL_CREATOR_AGREEMENT)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-handshake"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Content Creator Agreement</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'copyright' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('copyright', ROUTES.LEGAL_COPYRIGHT)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-copyright"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Copyright Policy</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'compliance' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('compliance', ROUTES.LEGAL_COMPLIANCE)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-gavel"></i>
                  </div>
                  <span className="modern-side-menu-item-text">18 U.S.C. § 2257</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'referral' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('referral', ROUTES.LEGAL_REFERRAL)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-gift"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Referral Program Terms</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'infringer' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('infringer', ROUTES.LEGAL_INFRINGER)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-ban"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Repeat Infringer Policy</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="modern-side-menu-section">
            <div className="modern-side-menu-section-header">
              <div className="modern-side-menu-section-title-wrapper">
                <div className="modern-side-menu-section-icon">
                  <i className="fa-solid fa-question-circle"></i>
                </div>
                <h2 className="modern-side-menu-section-title">FAQ</h2>
              </div>
            </div>
            <div className="modern-side-menu-items">
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'faq' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('faq', ROUTES.FAQ)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-question-circle"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Frequently Asked Questions</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
            </div>
          </section>

          {/* Complaints Section */}
          <section className="modern-side-menu-section">
            <div className="modern-side-menu-section-header">
              <div className="modern-side-menu-section-title-wrapper">
                <div className="modern-side-menu-section-icon">
                  <i className="fa-solid fa-exclamation-triangle"></i>
                </div>
                <h2 className="modern-side-menu-section-title">Complaints</h2>
              </div>
            </div>
            <div className="modern-side-menu-items">
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'complaint' ? 'active' : ''}`}
                onClick={() => window.open('https://www.ccbillcomplaintform.com/ccbill/form/CCBillContentRemovalRequest1/formperma/sBK2jfIoZWAFw2hRRt5Rv2PQncscFzpvOH6bPcwopas', '_blank')}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-exclamation-circle"></i>
                  </div>
                  <span className="modern-side-menu-item-text">DMCA Protection</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="modern-side-menu-section modern-side-menu-last-section">
            <div className="modern-side-menu-section-header">
              <div className="modern-side-menu-section-title-wrapper">
                <div className="modern-side-menu-section-icon">
                  <i className="fa-solid fa-envelope"></i>
                </div>
                <h2 className="modern-side-menu-section-title">Contact</h2>
              </div>
            </div>
            <div className="modern-side-menu-items">
              <div
                className={`modern-side-menu-item ${activeMenuItem === 'contact' ? 'active' : ''}`}
                onClick={() => handleMenuItemClick('contact', ROUTES.CONTACT_US)}
              >
                <div className="modern-side-menu-item-content">
                  <div className="modern-side-menu-item-icon">
                    <i className="fa-solid fa-file-lines"></i>
                  </div>
                  <span className="modern-side-menu-item-text">Contact us</span>
                </div>
                <i className="fa-solid fa-chevron-right modern-side-menu-item-arrow"></i>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="modern-side-menu-footer">
          {isLoggedIn && (
            <button className="modern-side-menu-logout-btn" onClick={handleLogout}>
              <div className="modern-side-menu-logout-icon">
                <i className="fa-solid fa-sign-out-alt"></i>
              </div>
              <span className="modern-side-menu-logout-text">Logout</span>
            </button>
          )}
          <div className="modern-side-menu-app-info">
            {/* <div className="modern-side-menu-version-info">
              <div className="modern-side-menu-status-dot"></div>
              <p className="modern-side-menu-version-text">
                App Version {CONFIG.APP_VERSION}
              </p>
            </div> */}

            <SocialInfo />

            <p className="modern-side-menu-copyright">
              © {new Date().getFullYear()} Prestige International, LLC. All rights reserved.
            </p>

            <p className="modern-side-menu-copyright">
              Juicy House®
            </p>


          </div>
        </footer>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
    creator: state.creator?.creator
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SideMenu); 